/* eslint-disable react/prop-types */
import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import React, { useContext, useEffect, useState } from 'react'
import { Col, FormGroup, Input, Row, Alert } from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  packagingInitialState,
  plantYogurtAltTypeOptions,
  fatLevelOptions,
  flavorOptions,
} from './constants'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import Switch from 'react-switch'
import Select from 'react-select'
import PlantYogurtProduct from 'models/PlantYogurtProduct'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const PlantYogurtPackage = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const { onChange, setSelect, setSwitch, asterisk } = useFormUtils()

  const methods = useFormContext()

  useEffect(() => {
    setScrollToTop(true)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      current?.category === packagingInitialState.category &&
      current?.subCategory === packagingInitialState.subCategory
    ) {
      const {
        category,
        subCategory,
        PlantYogurtAltType,
        OtherPlantYogurtAltType,
        FatLevel,
        Flavor,
        OtherFlavor,
        Size,
        MultiPackageType,
        MultiPackageCount,
      } = current

      setPackaging({
        category,
        subCategory,
        PlantYogurtAltType: {
          value: PlantYogurtAltType || '',
          label:
            PlantYogurtAltType.length > 0
              ? PlantYogurtAltType
              : 'Plant-Based Yogurt Alternative Type',
        },
        OtherPlantYogurtAltType,
        FatLevel: {
          value: FatLevel || '',
          label: FatLevel.length > 0 ? FatLevel : 'Fat Level',
        },
        Flavor: {
          value: Flavor || '',
          label: Flavor.length > 0 ? Flavor : 'Flavor',
        },
        OtherFlavor,
        Size,
        MultiPackageType: MultiPackageType ?? false,
        MultiPackageCount,
      })
    } else if (!updating) {
      setCurrent(PlantYogurtProduct)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  useEffect(() => {
    if (updating) {
      if (current?.PlantYogurtAltType === 'Other') {
        methods.setValue(
          'OtherPlantYogurtAltType',
          current.OtherPlantYogurtAltType
        )
      }
      if (current?.Flavor === 'Other') {
        methods.setValue('OtherFlavor', current.OtherFlavor)
      }
      if (current?.MultiPackageType) {
        methods.setValue('MultiPackageCount', current.MultiPackageCount)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>Plant-Based Yogurt Alternative Type {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="PlantYogurtAltType"
            placeholder={'Select Plant-Based Yogurt Alternative Type'}
            options={plantYogurtAltTypeOptions}
            onChange={([selected]) => {
              setSelect(selected, 'PlantYogurtAltType')
              return selected
            }}
            control={methods.control}
            rules={{ required: true }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="PlantYogurtAltType"
              message={'⚠ Please select a plant-based yogurt alternative type.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>

        {current?.PlantYogurtAltType === 'Other' && (
          <FormGroup>
            <p>
              <strong>
                Other Plant-Based Yogurt Alternative Type (Please Specify){' '}
                {asterisk}
              </strong>
            </p>
            <Controller
              as={Input}
              name="OtherPlantYogurtAltType"
              placeholder={'Other Plant-Based Yogurt Alternative Type'}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              control={methods.control}
              rules={{
                maxLength: {
                  value: 15,
                  message:
                    '⚠ Other Plant-Based yogurt alternative type exceeds 15 characters.',
                },
                required: {
                  value: true,
                  message:
                    '⚠ Other plant-based yogurt alternative type is required',
                },
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="OtherPlantYogurtAltType"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}

        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            name="Size"
            onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
            control={methods.control}
            rules={{
              required: '⚠ Please enter product size',
              maxLength: { value: 5, message: '⚠ Size exceeds 5 characters' },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            defaultValue={packaging.Size}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <ErrorMessage
            errors={methods.errors}
            name="Size"
            as={<Alert color="danger" className="mt-3" />}
          />
        </FormGroup>
      </Col>
      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>Fat Level {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="FatLevel"
            placeholder={'Select Fat Level'}
            options={fatLevelOptions}
            onChange={([selected]) => {
              setSelect(selected, 'FatLevel')
              return selected
            }}
            control={methods.control}
            rules={{ required: true }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="FatLevel"
              message={'⚠ Please select a fat level.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>

        <FormGroup>
          <p>
            <strong>Flavor {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="Flavor"
            placeholder={'Select Flavor'}
            options={flavorOptions}
            onChange={([selected]) => {
              setSelect(selected, 'Flavor')
              return selected
            }}
            control={methods.control}
            rules={{ required: true }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Flavor"
              message={'⚠ Please select a flavor.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>

        {current?.Flavor === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Flavor (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="OtherFlavor"
              placeholder={'Other Flavor'}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              control={methods.control}
              rules={{
                maxLength: {
                  value: 15,
                  message: '⚠ Other flavor exceeds 15 characters.',
                },
                required: {
                  value: true,
                  message: '⚠ Other flavor is required',
                },
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="OtherFlavor"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <FormGroup>
          <p>
            <strong>Multi-pack</strong>
          </p>
          <Controller
            as={Switch}
            name="MultiPackageType"
            id="MultiPackageType"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.MultiPackageType}
          />
        </FormGroup>
        <br />
        {current?.MultiPackageType === true && (
          <FormGroup>
            <p>
              <strong>Multi-pack Count {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="MultiPackageCount"
              placeholder={'Multi-pack Count'}
              onKeyDown={e => {
                if (
                  isNaN(parseInt(e.key)) &&
                  ![
                    'Backspace',
                    'Delete',
                    'Tab',
                    'ArrowLeft',
                    'ArrowRight',
                  ].includes(e.key)
                ) {
                  e.preventDefault()
                }
              }}
              control={methods.control}
              rules={{
                maxLength: { value: 2, message: '⚠ 2 characters max.' },
                required: {
                  value: true,
                  message: '⚠ Multi pack count is required.',
                },
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="MultiPackageCount"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
      </Col>
    </Row>
  )
}

export default PlantYogurtPackage
